import { type SortingState } from "@tanstack/react-table";
import { useMemo, useState } from "react";

/**
 * A hook for managing sorting state in data tables that integrates with TanStack Table and GraphQL APIs
 *
 * @template TColumnId - The type of column ID that will be used for sorting
 * @param initialSort - The initial sorting configuration
 * @returns Object containing:
 * - sorting: TanStack Table sorting state
 * - onSortingChange: Function to update sorting state
 * - sortBy: API-ready sort parameter for GraphQL queries
 *
 * @example
 * ```tsx
 * const { sorting, onSortingChange, sortBy } = useTableSorting({ id: "dateOpened", desc: true });
 * const { sorting, onSortingChange, sortBy } = useTableSorting({ id: "businessName", desc: false });
 *
 * // Use with TanStack Table
 * <DataTable
 *   sortConfig={{ sorting, onSortingChange }}
 *   {...otherProps}
 * />
 *
 * // Use with GraphQL query
 * const variables = {
 *   sortBy,
 *   ...otherVariables
 * };
 * ```
 */
export function useTableSorting<TColumnId extends string>(initialSort: { id: TColumnId; desc: boolean }) {
  const { id: initialColumnId, desc: initialDesc } = initialSort;
  const [sorting, setSorting] = useState<SortingState>([initialSort]);

  const sortBy = useMemo(
    () => [
      sorting[0]
        ? { desc: sorting[0].desc, field: sorting[0].id as TColumnId }
        : { desc: initialDesc, field: initialColumnId as TColumnId },
    ],
    [sorting, initialColumnId, initialDesc],
  );

  return {
    sorting,
    onSortingChange: setSorting,
    sortBy,
  };
}
